import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "@/lib/utils";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    ref={ref}
    data-slot="sheet-overlay"
    className={cn(
      "aero:data-[state=open]:animate-in aero:data-[state=closed]:animate-out aero:data-[state=closed]:fade-out-0 aero:data-[state=open]:fade-in-0 aero:fixed aero:inset-0 aero:z-50 aero:bg-black/50",
      className
    )}
    {...props}
  />
));
SheetOverlay.displayName = "SheetOverlay";

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & {
    side?: "top" | "right" | "bottom" | "left";
  }
>(({ className, children, side = "right", ...props }, ref) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      ref={ref}
      data-slot="sheet-content"
      className={cn(
        "aero:bg-background aero:data-[state=open]:animate-in aero:data-[state=closed]:animate-out aero:fixed aero:z-50 aero:flex aero:flex-col aero:gap-4 aero:shadow-lg aero:transition aero:ease-in-out aero:data-[state=closed]:duration-300 aero:data-[state=open]:duration-500",
        side === "right" &&
          "aero:data-[state=closed]:slide-out-to-right aero:data-[state=open]:slide-in-from-right aero:inset-y-0 aero:right-0 aero:h-full aero:w-3/4 aero:border-l aero:sm:max-w-sm",
        side === "left" &&
          "aero:data-[state=closed]:slide-out-to-left aero:data-[state=open]:slide-in-from-left aero:inset-y-0 aero:left-0 aero:h-full aero:w-3/4 aero:border-r aero:sm:max-w-sm",
        side === "top" &&
          "aero:data-[state=closed]:slide-out-to-top aero:data-[state=open]:slide-in-from-top aero:inset-x-0 aero:top-0 aero:h-auto aero:border-b",
        side === "bottom" &&
          "aero:data-[state=closed]:slide-out-to-bottom aero:data-[state=open]:slide-in-from-bottom aero:inset-x-0 aero:bottom-0 aero:h-auto aero:border-t",
        className
      )}
      {...props}
    >
      {children}
      <SheetPrimitive.Close className="aero:ring-offset-background aero:focus:ring-ring aero:data-[state=open]:bg-secondary aero:absolute aero:top-4 aero:right-4 aero:!rounded-xs aero:opacity-70 aero:transition-opacity aero:hover:opacity-100 aero:focus:ring-2 aero:focus:ring-offset-2 aero:focus:outline-hidden aero:disabled:pointer-events-none">
        <XIcon className="aero:size-4" />
        <span className="aero:sr-only">Close</span>
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  </SheetPortal>
));
SheetContent.displayName = "SheetContent";

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("aero:flex aero:flex-col aero:gap-1.5 aero:p-4", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        "aero:mt-auto aero:flex aero:flex-col aero:gap-2 aero:p-4",
        className
      )}
      {...props}
    />
  );
}

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    data-slot="sheet-title"
    className={cn("aero:text-foreground aero:font-semibold", className)}
    {...props}
  />
));
SheetTitle.displayName = "SheetTitle";

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    data-slot="sheet-description"
    className={cn("aero:text-muted-foreground aero:text-sm", className)}
    {...props}
  />
));
SheetDescription.displayName = "SheetDescription";

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
